import * as Sentry from "@sentry/react";
import jwt_decode from "jwt-decode";
import { getAnalyticsContext } from "./analytics";
import { peekSessionToken } from "./sessionToken";

/**
 * Structured Sentry log events. Keep in sync with docs/sentry-logging-registry.md.
 */
export type SentryLogEvent =
  | "schedule.initial_pull"
  | "schedule.refreshed"
  | "patient.created"
  | "recording.status_changed_to_processing"
  | "recording.permission_denied"
  | "recording.started"
  | "recording.stopped_invalid_chunks"
  | "recording.empty_first_chunk"
  | "recording.conversation_upload_succeeded"
  | "recording.upload_failed_no_chunks"
  | "recording.discarded"
  | "prechart.submitted"
  | "macro.created"
  | "macro.batch_uploaded"
  | "macro.updated"
  | "macro.deleted"
  | "security.csp_violation"
  | "security.framed";

export type PermissionDenialReason = "user" | "system" | "dismissed" | "other";

/** Map a getUserMedia/getDisplayMedia denial to a stable enum instead of logging the raw browser message. */
export function permissionDenialReason(err: unknown): PermissionDenialReason {
  const message = err instanceof Error ? err.message : String(err ?? "");
  if (/by system/i.test(message)) return "system";
  if (/dismissed/i.test(message)) return "dismissed";
  if (/denied|not allowed/i.test(message)) return "user";
  return "other";
}

type Primitive = string | number | boolean;

export type LogAttributes = Record<string, Primitive | null | undefined>;

const MAX_STRING_LENGTH = 200;

function getUserIdFromToken(): string | undefined {
  try {
    // Not getSessionToken: a log must never be what reports, and acts on, a lost token.
    const token = peekSessionToken();
    if (!token) return undefined;
    const decoded = jwt_decode<{ user?: { id?: string } }>(token);
    return decoded?.user?.id;
  } catch {
    return undefined;
  }
}

function safeAnalyticsContext(): LogAttributes {
  try {
    return getAnalyticsContext();
  } catch {
    return {};
  }
}

export function sanitizeAttributes(attributes: LogAttributes): Record<string, Primitive> {
  const result: Record<string, Primitive> = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (value === null || value === undefined) continue;

    if (typeof value === "string") {
      result[key] =
        value.length > MAX_STRING_LENGTH ? `${value.slice(0, MAX_STRING_LENGTH)}…` : value;
      continue;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      result[key] = value;
    }
  }

  return result;
}

function buildAttributes(event: SentryLogEvent, attributes: LogAttributes) {
  const base: LogAttributes = {
    event,
    user_id: getUserIdFromToken(),
    ...safeAnalyticsContext(),
  };

  return sanitizeAttributes({ ...base, ...attributes });
}

function send(
  level: "info" | "warn",
  message: string,
  event: SentryLogEvent,
  attributes: LogAttributes
) {
  // Logging must never break the user flow, so any SDK failure is swallowed.
  try {
    Sentry.logger[level](message, buildAttributes(event, attributes));
  } catch {
    // no-op
  }
}

/**
 * Log a normal or expected application state.
 * @param message Short, stable, human-readable description ("Recording started").
 * @param event Machine-readable event key for querying ("recording.started").
 * @param attributes Event-specific primitives. Include patient_id when in a patient context.
 */
export function logInfo(message: string, event: SentryLogEvent, attributes: LogAttributes = {}) {
  send("info", message, event, attributes);
}

/**
 * Log a degraded or abnormal state the app handled but engineering should be able to investigate.
 */
export function logWarn(message: string, event: SentryLogEvent, attributes: LogAttributes = {}) {
  send("warn", message, event, attributes);
}
